import {MouseEventHandler} from 'react';
import {Button} from 'components/ui';
import {StudentContestResultData} from 'APIs/letrusAPI/studentContestResult/types';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {certificateAssets} from './certificateAssets';
import {downloadContestCertificate} from './downloadContestCertificate';
import {getContestCertificateViewModel} from './certificateViewModel';
import styles from './ContestCertificateCard.module.scss';

interface ContestCertificateCardProps {
  result: StudentContestResultData | null;
}

function ContestCertificateCard({
  result,
}: ContestCertificateCardProps): JSX.Element | null {
  const {trackEvent} = useTrackEvent();
  const certificate = getContestCertificateViewModel(result);

  if (!certificate) {
    return null;
  }

  const handleDownloadCertificate: MouseEventHandler<Element> = () => {
    trackEvent('downloaded_contest_certificate_pdf_student');

    downloadContestCertificate(certificate);
  };

  return (
    <section className={styles.wrapper} aria-labelledby="certificate-title">
      <h2 id="certificate-title" className={styles.title}>
        Seu certificado
      </h2>

      <div className={styles.mobileSummary}>
        <div
          className={styles.summaryThumbnail}
          style={{
            backgroundImage: `url(${certificate.backgroundImage})`,
          }}
          aria-hidden="true"
        />

        <div className={styles.summaryContent}>
          <p className={styles.summaryEyebrow}>Prévia do certificado</p>
          <h3 className={styles.summaryTitle}>{certificate.title}</h3>
          <p className={styles.summaryText}>
            Certificado pronto para baixar em PDF.
          </p>
        </div>
      </div>

      <div className={styles.previewFrame}>
        <div
          className={styles.previewCanvas}
          style={{
            backgroundImage: `url(${certificate.backgroundImage})`,
          }}
        >
          <div className={styles.certificatePanel}>
            <header className={styles.certificateHeader}>
              <h3 className={styles.certificateTitle}>{certificate.title}</h3>
              {certificate.subtitle && (
                <p className={styles.certificateSubtitle}>
                  {certificate.subtitle}
                </p>
              )}
            </header>

            <section className={styles.certificateBody}>
              {certificate.lines.map((line) => (
                <p
                  key={line.segments.map((segment) => segment.text).join('|')}
                  className={styles.certificateLine}
                >
                  {line.segments.map((segment) => (
                    <span
                      key={`${segment.text}-${
                        segment.bold ? 'bold' : 'regular'
                      }`}
                      className={segment.bold ? styles.segmentBold : ''}
                    >
                      {segment.text}
                    </span>
                  ))}
                </p>
              ))}
            </section>

            <footer
              className={`${styles.certificateFooter} ${
                certificate.medalImage ? '' : styles.noMedal
              }`}
            >
              <div className={styles.signatureBlock}>
                <img
                  src={certificateAssets.signature}
                  alt="Assinatura de Luis Junqueira"
                  className={styles.signatureImage}
                />
                <span className={styles.signatureLine} />
                <span className={styles.signatureText}>
                  Luis Junqueira - Cofundador da Letrus
                </span>
              </div>

              {!!certificate.medalImage && (
                <img
                  src={certificate.medalImage}
                  alt="Medalha"
                  className={styles.medalImage}
                />
              )}

              <div className={styles.logosBlock}>
                <img
                  src={certificateAssets.letrusLogo}
                  alt="Letrus"
                  className={styles.letrusLogo}
                />
                <img
                  src={certificateAssets.olympicsLogo}
                  alt="Linguagem em Ação"
                  className={styles.olympicsLogo}
                />
              </div>
            </footer>
          </div>
        </div>
      </div>

      <div className={styles.downloadButtonRow}>
        <Button
          variant="primary"
          text="Baixar PDF"
          leftIcon={{icon: ['fas', 'download']}}
          onClick={handleDownloadCertificate}
        />
      </div>
    </section>
  );
}

export default ContestCertificateCard;
